/**
 * Step3BaselineSelection
 *
 * Changes from previous version:
 *   - Preset baseline cards now show monthly hours (annualHrs ÷ 12) as editable
 *     inputs per service per role
 *   - Edits stored as monthly values in local `overrides` state per baseline
 *   - When "Use This Baseline →" is clicked, edited monthly hours flow directly
 *     into Step 4 service cards (no ×12 conversion)
 *   - buildServicesFromBaseline is bypassed for preset cards — we build services
 *     directly from monthly role hours so Step 4 gets exactly what the user set
 *   - Custom editor unchanged
 */

import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  PlusCircleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  TrashIcon,
  BookmarkIcon,
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolid,
  BookmarkIcon as BookmarkSolid,
} from '@heroicons/react/24/solid';
import { BASELINES, SERVICE_NAME_MAP } from '../../constants/bidWizardConstants';

// ─── Constants ────────────────────────────────────────────────────────────────

const SERVICES = [
  'Advertising', 'Marketing Materials', 'Media', 'Newsletters', 'Nominations',
  'SEO', 'Social Media', 'Speaking', 'Strategic Marketing', 'Webinars', 'Website',
];

const DEFAULT_RATES = {
  srAM: 175, acctMgr: 150, asstAM: 75, mktgSupport: 50, principal: 275, freelance: 75,
};

const ROLE_LABELS = {
  srAM: 'Sr. Account Manager', acctMgr: 'Account Manager', asstAM: 'Asst. Account Manager',
  mktgSupport: 'Marketing Support', principal: 'Principal', freelance: 'Freelance',
};

const ROLE_SHORT = {
  srAM: 'Sr.AM', acctMgr: 'AM', asstAM: 'AAM', mktgSupport: 'Mktg', principal: 'Prin.', freelance: 'Free.',
};

const ROLE_DOTS = {
  srAM: 'bg-violet-500', acctMgr: 'bg-brightblue-500', asstAM: 'bg-teal-500',
  mktgSupport: 'bg-brand-500', principal: 'bg-pink-500', freelance: 'bg-grey-500',
};

// Canonical role name → camelCase key used in DEFAULT_RATES / ROLE_LABELS
const CANONICAL_TO_KEY = {
  'Principal':     'principal',
  'Sr AM':         'srAM',
  'Acct Mgr':      'acctMgr',
  'Asst Acct Mgr': 'asstAM',
  'Mktg Support':  'mktgSupport',
  'Admin Support': 'admSupport',
  'Freelance':     'freelance',
};

const COLORS = {
  violet: { border: 'border-violet-500', ring: 'ring-violet-500', bg: 'bg-violet-50', iconBg: 'bg-violet-100', iconHover: 'group-hover:bg-violet-200', icon: 'text-violet-600', badge: 'bg-violet-100 text-violet-700', bar: 'bg-violet-500', text: 'text-violet-700' },
  emerald: { border: 'border-teal-500', ring: 'ring-teal-500', bg: 'bg-teal-50', iconBg: 'bg-teal-100', iconHover: 'group-hover:bg-teal-200', icon: 'text-teal-600', badge: 'bg-teal-100 text-teal-700', bar: 'bg-teal-500', text: 'text-teal-700' },
  blue: { border: 'border-brightblue-500', ring: 'ring-brightblue-500', bg: 'bg-brightblue-50', iconBg: 'bg-brightblue-100', iconHover: 'group-hover:bg-brightblue-200', icon: 'text-brightblue-600', badge: 'bg-brightblue-100 text-brightblue-700', bar: 'bg-brightblue-500', text: 'text-brightblue-700' },
  amber: { border: 'border-brand-500', ring: 'ring-brand-500', bg: 'bg-brand-50', iconBg: 'bg-brand-100', iconHover: 'group-hover:bg-brand-200', icon: 'text-brand-600', badge: 'bg-brand-100 text-brand-700', bar: 'bg-brand-500', text: 'text-brand-700' },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toStep4Category = (name) => SERVICE_NAME_MAP[name] ?? name;

/**
 * Build Step 4 services[] from monthly role hours.
 * monthlyRoleHrsByService: { serviceName: { canonicalRole: monthlyHrs } }
 */
const buildServicesFromMonthlyHours = (monthlyRoleHrsByService) => {
  const services = [];
  for (const [serviceName, roleHrs] of Object.entries(monthlyRoleHrsByService)) {
    const items = Object.entries(roleHrs)
      .filter(([, hrs]) => (parseFloat(hrs) || 0) > 0)
      .map(([role, hrs]) => {
        const h = parseFloat(hrs);
        return {
          role,
          hours:     h,
          minHours:  parseFloat((h * 0.95).toFixed(1)),
          maxHours:  parseFloat((h * 1.05).toFixed(1)),
          autoRange: true,
          notes:     'From baseline',
        };
      })
      .sort((a, b) => b.hours - a.hours);

    if (!items.length) continue;
    services.push({ category: toStep4Category(serviceName), items });
  }
  return services;
};

/**
 * Build the default monthly role hours for a preset baseline.
 * Returns { serviceName: { canonicalRole: monthlyHrs } }
 */
const buildDefaultMonthlyHours = (baseline) => {
  const result = {};
  for (const [serviceName, data] of Object.entries(baseline.services)) {
    if (!data.annualHrs) continue;
    const roleHrs = data.roleHrs || {};
    const serviceMonthly = {};
    for (const [canonicalRole, annualHrs] of Object.entries(roleHrs)) {
      if ((parseFloat(annualHrs) || 0) === 0) continue;
      serviceMonthly[canonicalRole] = parseFloat((annualHrs / 12).toFixed(2));
    }
    if (Object.keys(serviceMonthly).length > 0) {
      result[serviceName] = serviceMonthly;
    }
  }
  return result;
};

// ─── Tooltip ──────────────────────────────────────────────────────────────────

const Tooltip = ({ content, children, width = 'w-72' }) => {
  const [visible, setVisible] = useState(false);
  const [alignment, setAlignment] = useState('center');
  const triggerRef = useRef(null);

  const measure = useCallback(() => {
    if (!triggerRef.current) return;
    const rect  = triggerRef.current.getBoundingClientRect();
    const vw    = window.innerWidth;
    const tipW  = parseInt(width.replace('w-', ''), 10) * 4;
    const center = rect.left + rect.width / 2;
    if (center - tipW / 2 < 8)           setAlignment('left');
    else if (center + tipW / 2 > vw - 8) setAlignment('right');
    else                                  setAlignment('center');
  }, [width]);

  const posClass = { center: 'left-1/2 -translate-x-1/2', left: 'left-0', right: 'right-0' }[alignment];

  return (
    <div ref={triggerRef} className="relative inline-flex"
      onMouseEnter={() => { measure(); setVisible(true); }}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => { measure(); setVisible(true); }}
      onBlur={() => setVisible(false)}
    >
      {children}
      {visible && (
        <div className={`absolute top-full mt-2 ${posClass} ${width} bg-darkblue-500 text-white text-xs rounded-lg px-3 py-2.5 z-50 shadow-xl leading-relaxed pointer-events-none`}>
          {content}
        </div>
      )}
    </div>
  );
};

// ─── Shared helpers ───────────────────────────────────────────────────────────

const GuidelineDot = ({ pass }) => (
  <div className={`w-3 h-3 rounded-full shrink-0 flex items-center justify-center ${pass ? 'bg-green-500' : 'bg-pink-500'}`}>
    {pass
      ? <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 12 12"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      : <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 12 12"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>}
  </div>
);

// ─── Editable Monthly Hours Panel ─────────────────────────────────────────────

const EditableMonthlyHours = ({ baseline, monthlyHours, onUpdate, colorBar }) => {
  const services = Object.keys(monthlyHours);

  const totalMonthlyHrs = useMemo(() =>
    services.reduce((total, svc) =>
      total + Object.values(monthlyHours[svc]).reduce((s, h) => s + (parseFloat(h) || 0), 0),
    0), [monthlyHours]);

  const handleChange = (serviceName, role, value) => {
    onUpdate({
      ...monthlyHours,
      [serviceName]: {
        ...monthlyHours[serviceName],
        [role]: value,
      },
    });
  };

  const handleReset = () => {
    onUpdate(buildDefaultMonthlyHours(baseline));
  };

  return (
    <div className="border-t border-grey-200 p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-darkblue-500 flex items-center gap-1.5">
          <PencilSquareIcon className="w-3.5 h-3.5 text-grey-400" />
          Monthly Hours by Service &amp; Role
        </p>
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold text-darkblue-500">
            {totalMonthlyHrs.toFixed(1)} hrs/mo total
          </span>
          <button
            onClick={handleReset}
            className="text-xs text-grey-400 hover:text-pink-500 transition-colors flex items-center gap-1"
          >
            <ArrowPathIcon className="w-3 h-3" /> Reset
          </button>
        </div>
      </div>

      {/* Service rows */}
      <div className="space-y-3">
        {services.map((serviceName) => {
          const roles = monthlyHours[serviceName];
          const svcTotal = Object.values(roles).reduce((s, h) => s + (parseFloat(h) || 0), 0);

          return (
            <div key={serviceName} className="bg-white border border-grey-100 rounded-lg p-3">
              {/* Service name + total */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-darkblue-500">{serviceName}</span>
                <span className="text-xs text-grey-500 tabular-nums">{svcTotal.toFixed(1)} hrs/mo</span>
              </div>
              {/* Role inputs */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                {Object.entries(roles).map(([canonicalRole, hrs]) => {
                  const key = CANONICAL_TO_KEY[canonicalRole] || canonicalRole;
                  const dot = ROLE_DOTS[key] || 'bg-grey-300';
                  return (
                    <div key={canonicalRole} className="flex items-center gap-1.5">
                      <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
                      <span className="text-xs text-grey-500 flex-1 truncate">{canonicalRole}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.5"
                        value={hrs}
                        onChange={(e) => handleChange(serviceName, canonicalRole, e.target.value)}
                        className="w-14 text-xs text-right border border-grey-200 rounded-md px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-darkblue-500 tabular-nums"
                      />
                      <span className="text-xs text-grey-400 shrink-0">h</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-grey-400">
        These monthly hours will be used directly in Step 4.
      </p>
    </div>
  );
};

// ─── Preset Baseline Card ─────────────────────────────────────────────────────

const BaselineCard = ({ baseline, isSelected, onSelect, monthlyHours, onMonthlyHoursUpdate }) => {
  const [expanded,     setExpanded]     = useState(false);
  const [editingHours, setEditingHours] = useState(false);

  const c      = COLORS[baseline.color];
  const Icon   = baseline.icon;
  const maxHrs = Math.max(...SERVICES.map(s => baseline.services[s]?.annualHrs || 0), 1);

  // Total monthly hours from current (possibly edited) state
  const totalMonthlyHrs = useMemo(() =>
    Object.values(monthlyHours).reduce((total, roles) =>
      total + Object.values(roles).reduce((s, h) => s + (parseFloat(h) || 0), 0),
    0), [monthlyHours]);

  const stratOk = baseline.guidelines.strategicPct >= 25 && baseline.guidelines.strategicPct <= 35;

  // Detect if user has edited away from defaults
  const defaults   = useMemo(() => buildDefaultMonthlyHours(baseline), [baseline]);
  const hasEdits   = useMemo(() => {
    try {
      return JSON.stringify(monthlyHours) !== JSON.stringify(defaults);
    } catch { return false; }
  }, [monthlyHours, defaults]);

  return (
    <div className={`relative border-2 rounded-xl overflow-hidden transition-all duration-200 ${
      isSelected
        ? `${c.border} shadow-lg ring-2 ${c.ring} ring-offset-1`
        : 'border-grey-300 hover:border-grey-500 hover:shadow-md'
    }`}>

      {isSelected && (
        <div className="absolute top-3 right-3 z-10">
          <CheckCircleSolid className={`w-5 h-5 ${c.text}`} />
        </div>
      )}

      {/* Main card body */}
      <button
        onClick={() => onSelect(baseline.id)}
        className={`w-full text-left p-5 group transition-colors ${isSelected ? c.bg : 'bg-white hover:bg-grey-50'}`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${c.iconBg} ${c.iconHover} transition-colors`}>
            <Icon className={`w-5 h-5 ${c.icon}`} />
          </div>
          <div className="flex-1 min-w-0 pr-6">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.badge}`}>{baseline.label}</span>
              <span className="text-xs text-grey-400">{baseline.tag}</span>
              {hasEdits && isSelected && (
                <span className="text-xs px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                  Edited
                </span>
              )}
            </div>
            <p className="text-xs text-grey-500 leading-snug">{baseline.description}</p>
          </div>
        </div>

        {/* Summary stats — show monthly hours from current state */}
        <div className="grid grid-cols-3 gap-2 mt-4 text-center">
          <div>
            <div className={`text-sm font-bold ${isSelected ? c.text : 'text-darkblue-500'}`}>
              ${baseline.monthlyFee.toLocaleString()}
            </div>
            <div className="text-xs text-grey-400">/ month est.</div>
          </div>
          <div>
            <div className={`text-sm font-bold ${isSelected ? c.text : 'text-darkblue-500'}`}>
              {totalMonthlyHrs.toFixed(1)}h
            </div>
            <div className="text-xs text-grey-400">/ month{hasEdits ? ' (edited)' : ''}</div>
          </div>
          <div>
            <div className={`text-sm font-bold ${isSelected ? c.text : 'text-darkblue-500'}`}>
              ${baseline.annualFee.toLocaleString()}
            </div>
            <div className="text-xs text-grey-400">/ year est.</div>
          </div>
        </div>
      </button>

      {/* Action row — View breakdown + Edit hours */}
      <div className={`flex border-t ${isSelected ? `border-grey-200 ${c.bg}` : 'border-grey-100 bg-white'}`}>
        <button
          onClick={() => { setExpanded(v => !v); if (editingHours) setEditingHours(false); }}
          className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium transition-colors border-r border-grey-100 ${
            isSelected ? `${c.text}` : 'text-grey-400 hover:text-grey-600'
          }`}
        >
          {expanded && !editingHours
            ? <><ChevronUpIcon className="w-3 h-3" /> Hide breakdown</>
            : <><ChevronDownIcon className="w-3 h-3" /> View breakdown</>}
        </button>
        <button
          onClick={() => { setEditingHours(v => !v); if (!isSelected) onSelect(baseline.id); setExpanded(false); }}
          className={`flex-1 flex items-center justify-center gap-1 py-2 text-xs font-medium transition-colors ${
            editingHours
              ? `${c.text} font-semibold`
              : isSelected
                ? `${c.text}`
                : 'text-grey-400 hover:text-grey-600'
          }`}
        >
          <PencilSquareIcon className="w-3 h-3" />
          {editingHours ? 'Hide editor' : 'Edit hours'}
        </button>
      </div>

      {/* Breakdown panel */}
      {expanded && !editingHours && (
        <div className={`p-4 border-t border-grey-200 space-y-3 ${isSelected ? c.bg : 'bg-grey-50'}`}>
          {/* Guideline checks */}
          <div className="pt-2 border-t border-grey-200">
            <p className="text-xs font-semibold text-grey-500 mb-2">Guideline Checks</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              {[
                { label: 'Principal < 5%',   pass: baseline.guidelines.principalUnder5  },
                { label: 'Freelance < 15%',  pass: baseline.guidelines.freelanceUnder15 },
                { label: 'Sr/AM/AAM < 70%',  pass: baseline.guidelines.seniorUnder70   },
                { label: `Strategic 25–35% (${baseline.guidelines.strategicPct}%)`, pass: stratOk },
              ].map(({ label, pass }) => (
                <div key={label} className="flex items-center gap-1.5">
                  <GuidelineDot pass={pass} />
                  <span className="text-xs text-grey-500">{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Per-service role breakdown (read-only, shows defaults) */}
          <div className="space-y-2">
            {SERVICES.filter(s => (baseline.services[s]?.annualHrs || 0) > 0).map(s => {
              const svc     = baseline.services[s];
              const roleHrs = svc.roleHrs || {};
              const roleKeys = Object.keys(roleHrs).filter(r => roleHrs[r] > 0);
              const barPct  = maxHrs > 0 ? Math.round((svc.annualHrs / maxHrs) * 100) : 0;

              return (
                <div key={s} className="rounded-lg bg-white border border-grey-100 p-2.5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs font-medium text-grey-700 w-32 shrink-0 truncate">{s}</span>
                    <div className="flex-1 h-1.5 bg-grey-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${barPct}%` }} />
                    </div>
                    <span className="text-xs font-semibold text-grey-600 w-16 text-right shrink-0 tabular-nums">
                      {(svc.annualHrs / 12).toFixed(1)}h/mo
                    </span>
                  </div>
                  {roleKeys.length > 0 && (
                    <div className="flex flex-wrap gap-1 pl-0.5">
                      {roleKeys.map(role => {
                        const hrs      = roleHrs[role];
                        const moHrs    = (hrs / 12).toFixed(1);
                        const rolePct  = svc.annualHrs > 0 ? Math.round((hrs / svc.annualHrs) * 100) : 0;
                        const dot = {
                          'Principal': 'bg-pink-400', 'Sr AM': 'bg-violet-500', 'Acct Mgr': 'bg-brightblue-500',
                          'Asst Acct Mgr': 'bg-teal-500', 'Mktg Support': 'bg-brand-500',
                          'Admin Support': 'bg-grey-400', 'Freelance': 'bg-grey-400',
                        }[role] || 'bg-grey-300';
                        return (
                          <span key={role} className="inline-flex items-center gap-1 text-xs bg-grey-50 border border-grey-200 rounded px-1.5 py-0.5 text-grey-600">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
                            <span className="font-medium">{role}</span>
                            <span className="text-grey-400">{moHrs}h/mo</span>
                            <span className="text-grey-300">·{rolePct}%</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Editable monthly hours panel */}
      {editingHours && (
        <EditableMonthlyHours
          baseline={baseline}
          monthlyHours={monthlyHours}
          onUpdate={onMonthlyHoursUpdate}
          colorBar={c.bar}
        />
      )}
    </div>
  );
};

// ─── Saved Preset Pill ────────────────────────────────────────────────────────

const SavedPresetPill = ({ preset, isSelected, onLoad, onDelete }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-all ${
    isSelected
      ? 'border-darkblue-500 bg-darkblue-50 text-darkblue-700'
      : 'border-grey-200 bg-white text-grey-600 hover:border-darkblue-300 hover:bg-grey-50'
  }`}>
    <BookmarkSolid className={`w-3 h-3 shrink-0 ${isSelected ? 'text-darkblue-500' : 'text-grey-400'}`} />
    <button onClick={() => onLoad(preset)} className="flex-1 text-left font-medium truncate max-w-[120px]">{preset.label}</button>
    <span className="text-grey-400 shrink-0 tabular-nums">{preset.monthlyHrs}h/mo</span>
    <button onClick={() => onDelete(preset.id)} className="text-grey-300 hover:text-pink-500 transition-colors shrink-0 ml-1">
      <TrashIcon className="w-3 h-3" />
    </button>
  </div>
);

// ─── Custom Baseline Editor (unchanged) ──────────────────────────────────────

const ServiceMixRow = ({ service, hours, serviceMix, globalMix, maxHrs, onHoursChange, onMixChange, onResetMix }) => {
  const [expanded, setExpanded] = useState(false);
  const hrs         = parseFloat(hours) || 0;
  const pct         = maxHrs > 0 ? Math.round((hrs / maxHrs) * 100) : 0;
  const isStrategic = service === 'Strategic Marketing';
  const hasOverride = serviceMix !== null;
  const activeMix   = hasOverride ? serviceMix : globalMix;
  const mixTotal    = Object.values(activeMix).reduce((s, v) => s + (parseFloat(v) || 0), 0);
  const mixValid    = Math.round(mixTotal) === 100;

  return (
    <div className={`rounded-lg border transition-all duration-200 ${expanded ? 'border-grey-200 bg-grey-50' : 'border-transparent'}`}>
      <div className="flex items-center gap-2 px-2 py-1.5">
        <span className={`text-xs w-28 truncate shrink-0 ${isStrategic ? 'font-semibold text-darkblue-500' : 'text-grey-600'}`}>{service}</span>
        <div className="flex-1 h-1.5 bg-grey-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-300 ${isStrategic ? 'bg-violet-500' : 'bg-grey-300'}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <input type="number" min="0" step="0.5" value={hours}
            onChange={e => onHoursChange(service, e.target.value)} placeholder="0"
            className="w-14 text-xs text-right border border-grey-200 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-darkblue-500" />
          <span className="text-xs text-grey-400 w-3">h</span>
        </div>
        <Tooltip content={expanded ? 'Hide staff mix' : 'Set per-service staff mix'} width="w-44">
          <button onClick={() => setExpanded(v => !v)}
            className={`p-1 rounded transition-colors shrink-0 ${hasOverride ? 'text-brightblue-600 bg-brightblue-50 hover:bg-brightblue-100' : 'text-grey-400 hover:text-darkblue-500 hover:bg-grey-100'}`}>
            <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
          </button>
        </Tooltip>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-grey-600 flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full inline-block ${hasOverride ? 'bg-brightblue-500' : 'bg-grey-400'}`} />
              {hasOverride ? 'Custom mix' : 'Using global mix'}
            </span>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${mixValid ? 'bg-teal-100 text-teal-700' : mixTotal > 100 ? 'bg-pink-100 text-pink-600' : 'bg-grey-100 text-grey-500'}`}>
                {mixTotal.toFixed(0)}%
              </span>
              {hasOverride && (
                <button onClick={() => onResetMix(service)} className="text-xs text-grey-400 hover:text-pink-500 transition-colors flex items-center gap-0.5">
                  <ArrowPathIcon className="w-3 h-3" /> Reset
                </button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {Object.entries(ROLE_SHORT).map(([key, short]) => (
              <div key={key} className="flex items-center gap-1.5">
                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${ROLE_DOTS[key]}`} />
                <span className="text-xs text-grey-500 w-10 shrink-0">{short}</span>
                <input type="number" min="0" max="100" step="1" value={activeMix[key] ?? ''}
                  onChange={e => onMixChange(service, key, e.target.value)} placeholder="0"
                  className="w-12 text-xs text-right border border-grey-200 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-darkblue-500" />
                <span className="text-xs text-grey-400">%</span>
              </div>
            ))}
          </div>
          {!hasOverride && (
            <button onClick={() => onMixChange(service, '__copy_global__', null)}
              className="mt-1 w-full text-xs text-brightblue-600 hover:text-brightblue-700 border border-dashed border-brightblue-200 hover:border-brightblue-400 rounded py-1 transition-colors">
              Copy global mix as starting point
            </button>
          )}
        </div>
      )}
      {hrs > 0 && !expanded && (
        <div className="text-xs text-grey-400 text-right pr-8 -mt-0.5 pb-1">{(hrs / 12).toFixed(1)} h/mo</div>
      )}
    </div>
  );
};

const buildEmptyForm = () => ({
  name:      '',
  services:  Object.fromEntries(SERVICES.map(s => [s, { hours: '', serviceMix: null }])),
  globalMix: Object.fromEntries(Object.keys(DEFAULT_RATES).map(k => [k, ''])),
  rates:     { ...DEFAULT_RATES },
});

const CustomBaselineEditor = ({ isSelected, onSelect, onDataChange, savedPresets = [], onSavePreset, onDeletePreset }) => {
  const [form,      setForm]      = useState(buildEmptyForm());
  const [minimized, setMinimized] = useState(false);
  const [saveMode,  setSaveMode]  = useState(false);
  const [saveName,  setSaveName]  = useState('');
  const [loadedId,  setLoadedId]  = useState(null);

  const totalAnnualHrs = useMemo(() => SERVICES.reduce((s, svc) => s + (parseFloat(form.services[svc].hours) || 0), 0), [form.services]);
  const globalMixTotal = useMemo(() => Object.values(form.globalMix).reduce((s, v) => s + (parseFloat(v) || 0), 0), [form.globalMix]);

  const resolvedMixes = useMemo(() =>
    Object.fromEntries(SERVICES.map(s => [s, form.services[s].serviceMix !== null ? form.services[s].serviceMix : form.globalMix])),
    [form.services, form.globalMix]);

  const estimatedAnnualFee = useMemo(() =>
    SERVICES.reduce((total, s) => {
      const hrs = parseFloat(form.services[s].hours) || 0;
      if (!hrs) return total;
      const mix = resolvedMixes[s];
      const mixTotal = Object.values(mix).reduce((a, v) => a + (parseFloat(v) || 0), 0);
      if (!mixTotal) return total;
      return total + Object.entries(mix).reduce((acc, [role, pct]) => {
        const share = (parseFloat(pct) || 0) / mixTotal;
        const rateVal = parseFloat(form.rates[role]);
        const rate = (rateVal && rateVal > 0) ? rateVal : (DEFAULT_RATES[role] || 0);
        return acc + hrs * share * rate;
      }, 0);
    }, 0), [form.services, resolvedMixes, form.rates]);

  const estimatedMonthlyFee = Math.round(estimatedAnnualFee / 12);
  const principalPct = parseFloat(form.globalMix.principal) || 0;
  const freelancePct = parseFloat(form.globalMix.freelance) || 0;
  const seniorPct    = (parseFloat(form.globalMix.srAM) || 0) + (parseFloat(form.globalMix.acctMgr) || 0) + (parseFloat(form.globalMix.asstAM) || 0);
  const strategicHrs = parseFloat(form.services['Strategic Marketing'].hours) || 0;
  const strategicPct = totalAnnualHrs > 0 ? Math.round((strategicHrs / totalAnnualHrs) * 100) : 0;
  const checks = { principalUnder5: principalPct < 5, freelanceUnder15: freelancePct < 15, seniorUnder70: seniorPct < 70, strategicInRange: strategicPct >= 25 && strategicPct <= 35 };
  const globalOver = globalMixTotal > 100;
  const globalRemain = Math.round(100 - globalMixTotal);

  const buildPayload = useCallback((f, tHrs, annFee, moFee, lId) => ({
    id: lId || 'custom', label: f.name || 'Custom Baseline',
    annualHrs: tHrs, monthlyHrs: parseFloat((tHrs / 12).toFixed(1)),
    annualFee: Math.round(annFee), monthlyFee: moFee,
    services: Object.fromEntries(Object.entries(f.services).map(([k, v]) => [k, { annualHrs: parseFloat(v.hours) || 0, staffMix: v.serviceMix }])),
    globalMix: Object.fromEntries(Object.entries(f.globalMix).map(([k, v]) => [k, parseFloat(v) || 0])),
    rates: f.rates,
  }), []);

  const update = (next) => {
    setForm(next);
    const tHrs = SERVICES.reduce((s, svc) => s + (parseFloat(next.services[svc].hours) || 0), 0);
    const mixes = Object.fromEntries(SERVICES.map(s => [s, next.services[s].serviceMix !== null ? next.services[s].serviceMix : next.globalMix]));
    const annFee = SERVICES.reduce((total, s) => {
      const hrs = parseFloat(next.services[s].hours) || 0;
      if (!hrs) return total;
      const mix = mixes[s]; const mt = Object.values(mix).reduce((a, v) => a + (parseFloat(v) || 0), 0);
      if (!mt) return total;
      return total + Object.entries(mix).reduce((acc, [role, pct]) => {
        const rateVal = parseFloat(next.rates[role]); const rate = (rateVal && rateVal > 0) ? rateVal : (DEFAULT_RATES[role] || 0);
        return acc + hrs * ((parseFloat(pct) || 0) / mt) * rate;
      }, 0);
    }, 0);
    onDataChange?.(buildPayload(next, tHrs, annFee, Math.round(annFee / 12), loadedId));
  };

  const handleHoursChange = (svc, val) => update({ ...form, services: { ...form.services, [svc]: { ...form.services[svc], hours: val } } });
  const handleMixChange = (svc, role, val) => {
    const curr = form.services[svc];
    if (role === '__copy_global__') return update({ ...form, services: { ...form.services, [svc]: { ...curr, serviceMix: { ...form.globalMix } } } });
    const base = curr.serviceMix !== null ? curr.serviceMix : { ...form.globalMix };
    update({ ...form, services: { ...form.services, [svc]: { ...curr, serviceMix: { ...base, [role]: val } } } });
  };
  const handleResetMix  = (svc) => update({ ...form, services: { ...form.services, [svc]: { ...form.services[svc], serviceMix: null } } });
  const handleGlobalMix = (role, val) => update({ ...form, globalMix: { ...form.globalMix, [role]: val } });
  const handleRate      = (role, val) => update({ ...form, rates: { ...form.rates, [role]: val } });
  const handleName      = (val) => update({ ...form, name: val });
  const clearAll        = () => { setForm(buildEmptyForm()); setLoadedId(null); onDataChange?.(null); };
  const distributeEvenly = () => {
    const keys = Object.keys(form.globalMix); const base = Math.floor(100 / keys.length); const rem = 100 - base * keys.length;
    update({ ...form, globalMix: Object.fromEntries(keys.map((k, i) => [k, String(i === 0 ? base + rem : base)])) });
  };
  const handleLoadPreset = (preset) => {
    const next = buildEmptyForm(); next.name = preset.label;
    if (preset.globalMix) Object.entries(preset.globalMix).forEach(([k, v]) => { next.globalMix[k] = String(v); });
    if (preset.rates) Object.entries(preset.rates).forEach(([k, v]) => { next.rates[k] = v; });
    if (preset.services) Object.entries(preset.services).forEach(([svc, data]) => { if (next.services[svc] !== undefined) next.services[svc] = { hours: String(data.annualHrs || ''), serviceMix: data.staffMix || null }; });
    setLoadedId(preset.id); setForm(next); onSelect('custom'); setMinimized(false); update(next);
  };
  const handleSave = () => {
    if (!saveName.trim()) return;
    const payload = { ...buildPayload(form, totalAnnualHrs, estimatedAnnualFee, estimatedMonthlyFee, null), id: `custom_${Date.now()}`, label: saveName };
    onSavePreset?.(payload); setSaveMode(false); setSaveName(''); setLoadedId(payload.id);
  };

  const maxSvcHrs = Math.max(...SERVICES.map(s => parseFloat(form.services[s].hours) || 0), 1);
  const servicesWithOverride = SERVICES.filter(s => form.services[s].serviceMix !== null).length;
  const feeByRole = useMemo(() => {
    if (!globalMixTotal || !totalAnnualHrs) return null;
    return Object.entries(ROLE_LABELS).map(([key, label]) => {
      const pct = parseFloat(form.globalMix[key]) || 0;
      if (!pct) return null;
      return { key, label, fee: totalAnnualHrs * (pct / 100) * (parseFloat(form.rates[key]) || 0) };
    }).filter(Boolean);
  }, [form.globalMix, form.rates, totalAnnualHrs, globalMixTotal]);

  const hasMini = isSelected && totalAnnualHrs > 0;

  return (
    <div className={`col-span-1 sm:col-span-2 lg:col-span-3 border-2 rounded-xl overflow-hidden transition-all duration-200 ${
      isSelected ? 'border-darkblue-500 shadow-lg ring-2 ring-darkblue-500 ring-offset-1' : 'border-dashed border-grey-300 hover:border-grey-500 hover:shadow-md'
    }`}>
      <div className={`flex items-center transition-colors ${isSelected ? 'bg-grey-50' : 'bg-white hover:bg-grey-50'}`}>
        <button onClick={() => { onSelect('custom'); if (!isSelected) setMinimized(false); }} className="flex-1 text-left p-5 group">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-grey-100 group-hover:bg-grey-200 transition-colors">
              <PlusCircleIcon className="w-5 h-5 text-darkblue-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-grey-100 text-darkblue-500">Custom</span>
                <span className="text-xs text-grey-400">Build your own baseline</span>
                {servicesWithOverride > 0 && <span className="text-xs px-1.5 py-0.5 rounded-full bg-brightblue-100 text-brightblue-700">{servicesWithOverride} service override{servicesWithOverride > 1 ? 's' : ''}</span>}
              </div>
              {hasMini && minimized
                ? <p className="text-xs text-darkblue-500 font-medium">{totalAnnualHrs.toFixed(1)} hrs/yr{estimatedMonthlyFee > 0 && ` · $${estimatedMonthlyFee.toLocaleString()}/mo est.`}{form.name && ` · "${form.name}"`}</p>
                : <p className="text-xs text-grey-500 leading-snug">Define service hours, per-service staff mix &amp; billing rates — fee estimated live</p>}
            </div>
          </div>
        </button>
        <div className="flex items-center gap-2 pr-4 shrink-0">
          {isSelected && (
            <>
              <Tooltip content={minimized ? 'Expand editor' : 'Minimize editor'} width="w-32">
                <button onClick={() => setMinimized(v => !v)} className="p-1.5 rounded-lg text-grey-400 hover:text-darkblue-500 hover:bg-grey-100 transition-colors">
                  {minimized ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronUpIcon className="w-4 h-4" />}
                </button>
              </Tooltip>
              <CheckCircleSolid className="w-5 h-5 text-darkblue-500 shrink-0" />
            </>
          )}
          {!isSelected && <ChevronDownIcon className="w-4 h-4 text-grey-400 shrink-0" />}
        </div>
      </div>

      {savedPresets.length > 0 && (
        <div className="border-t border-grey-100 px-4 py-3 bg-grey-50">
          <p className="text-xs font-semibold text-grey-500 mb-2 flex items-center gap-1.5"><BookmarkSolid className="w-3 h-3 text-grey-400" /> Saved Presets</p>
          <div className="flex flex-wrap gap-2">{savedPresets.map(p => <SavedPresetPill key={p.id} preset={p} isSelected={loadedId === p.id} onLoad={handleLoadPreset} onDelete={onDeletePreset} />)}</div>
        </div>
      )}

      {isSelected && !minimized && (
        <div className="border-t border-grey-200">
          <div className="px-5 py-3 bg-darkblue-500 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><SparklesIcon className="w-4 h-4 text-grey-300" /><span className="text-xs font-semibold uppercase tracking-wide text-grey-300">Live Preview</span></div>
            <div className="flex flex-wrap gap-5">
              {[{ label: 'Hrs / Yr', val: totalAnnualHrs > 0 ? totalAnnualHrs.toFixed(1) : '—' }, { label: 'Hrs / Mo', val: totalAnnualHrs > 0 ? (totalAnnualHrs / 12).toFixed(1) : '—' }, { label: 'Est. Mo Fee', val: estimatedMonthlyFee > 0 ? `$${estimatedMonthlyFee.toLocaleString()}` : '—' }, { label: 'Est. Yr Fee', val: estimatedAnnualFee > 0 ? `$${Math.round(estimatedAnnualFee).toLocaleString()}` : '—' }].map(({ label, val }) => (
                <div key={label} className="text-center"><div className="text-sm font-bold text-white">{val}</div><div className="text-xs text-grey-400">{label}</div></div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              {totalAnnualHrs > 0 && (saveMode ? (
                <div className="flex items-center gap-2">
                  <input type="text" value={saveName} onChange={e => setSaveName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSave()} placeholder="Preset name…" autoFocus
                    className="text-xs border border-grey-500 rounded-md px-2 py-1 w-36 bg-darkblue-700 text-white placeholder-grey-400 focus:outline-none focus:ring-1 focus:ring-brand-400" />
                  <button onClick={handleSave} disabled={!saveName.trim()} className="text-xs font-semibold px-2.5 py-1 rounded-md bg-brand-500 hover:bg-brand-dark text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed">Save</button>
                  <button onClick={() => { setSaveMode(false); setSaveName(''); }} className="text-xs text-grey-400 hover:text-white transition-colors">✕</button>
                </div>
              ) : (
                <button onClick={() => { setSaveMode(true); setSaveName(form.name || ''); }} className="flex items-center gap-1.5 text-xs text-grey-300 hover:text-brand-300 transition-colors"><BookmarkIcon className="w-3.5 h-3.5" /> Save preset</button>
              ))}
              <button onClick={clearAll} className="flex items-center gap-1.5 text-xs text-grey-400 hover:text-pink-400 transition-colors"><TrashIcon className="w-3.5 h-3.5" /> Clear</button>
            </div>
          </div>

          <div className="p-5 bg-white grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-darkblue-500 mb-1.5">Baseline Name</label>
                <input type="text" value={form.name} onChange={e => handleName(e.target.value)} placeholder="e.g. Law Firm – Mid Retainer"
                  className="w-full text-sm border border-grey-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-darkblue-500 focus:border-transparent placeholder-grey-300" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs font-semibold text-darkblue-500">Service Hours <span className="font-normal text-grey-400">(annual)</span></label>
                    <Tooltip width="w-56" content="Click the sliders icon on any row to override the staff mix for that specific service.">
                      <InformationCircleIcon className="w-3.5 h-3.5 text-grey-300 hover:text-brightblue-500 cursor-help transition-colors" />
                    </Tooltip>
                  </div>
                  {totalAnnualHrs > 0 && <span className="text-xs text-grey-500">{totalAnnualHrs.toFixed(1)} total</span>}
                </div>
                <div className="space-y-0.5">
                  {SERVICES.map(s => (
                    <ServiceMixRow key={s} service={s} hours={form.services[s].hours} serviceMix={form.services[s].serviceMix}
                      globalMix={form.globalMix} maxHrs={maxSvcHrs} onHoursChange={handleHoursChange}
                      onMixChange={handleMixChange} onResetMix={handleResetMix} />
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs font-semibold text-darkblue-500">Global Staff Mix <span className="font-normal text-grey-400">(%)</span></label>
                    <Tooltip width="w-60" content="Default mix applied to all services. Override per-service using the sliders icon on each row.">
                      <InformationCircleIcon className="w-3.5 h-3.5 text-grey-300 hover:text-brightblue-500 cursor-help transition-colors" />
                    </Tooltip>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${globalMixTotal === 100 ? 'bg-teal-100 text-teal-700' : globalOver ? 'bg-pink-100 text-pink-600' : 'bg-grey-100 text-grey-500'}`}>
                    {globalMixTotal.toFixed(0)}% / 100%
                  </span>
                </div>
                {globalOver && <div className="flex items-center gap-1.5 text-xs text-pink-600 mb-3 p-2 bg-pink-50 rounded-lg border border-pink-200"><ExclamationTriangleIcon className="w-3.5 h-3.5 shrink-0" />Exceeds 100% — reduce by {Math.abs(globalRemain)}%</div>}
                {!globalOver && globalRemain > 0 && globalMixTotal > 0 && <div className="flex items-center gap-1.5 text-xs text-brand-600 mb-3 p-2 bg-brand-50 rounded-lg border border-brand-200"><InformationCircleIcon className="w-3.5 h-3.5 shrink-0" />{globalRemain}% remaining to allocate</div>}
                {globalMixTotal > 0 && (
                  <div className="h-3 rounded-full overflow-hidden flex mb-3 bg-grey-100">
                    {Object.entries(ROLE_DOTS).map(([key, dot]) => { const p = parseFloat(form.globalMix[key]) || 0; return p > 0 ? <div key={key} className={`${dot} h-full transition-all duration-300`} style={{ width: `${(p / Math.max(globalMixTotal, 100)) * 100}%` }} title={`${ROLE_LABELS[key]}: ${p}%`} /> : null; })}
                  </div>
                )}
                <div className="space-y-2">
                  {Object.entries(ROLE_LABELS).map(([key, label]) => (
                    <div key={key} className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${ROLE_DOTS[key]}`} />
                      <span className="text-xs text-grey-600 flex-1 truncate">{label}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <input type="number" min="0" max="100" step="1" value={form.globalMix[key]} onChange={e => handleGlobalMix(key, e.target.value)} placeholder="0"
                          className="w-14 text-xs text-right border border-grey-200 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-darkblue-500" />
                        <span className="text-xs text-grey-400 w-3">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <button onClick={distributeEvenly} className="mt-3 w-full text-xs text-grey-500 hover:text-darkblue-500 border border-dashed border-grey-300 hover:border-darkblue-500 rounded-lg py-1.5 transition-colors">Distribute evenly (~17% each)</button>
                {servicesWithOverride > 0 && (
                  <div className="mt-3 p-2.5 bg-brightblue-50 border border-brightblue-200 rounded-lg">
                    <p className="text-xs text-brightblue-700 font-medium flex items-center gap-1.5"><AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />{servicesWithOverride} service{servicesWithOverride > 1 ? 's' : ''} with custom mix</p>
                    <p className="text-xs text-brightblue-600 mt-0.5 leading-snug">{SERVICES.filter(s => form.services[s].serviceMix !== null).join(', ')}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-darkblue-500 mb-2">Hourly Rates <span className="font-normal text-grey-400">($/hr)</span></label>
                <div className="space-y-2">
                  {Object.entries(ROLE_LABELS).map(([key, label]) => {
                    const val = form.rates[key]; const isDefault = val === DEFAULT_RATES[key] || val === '';
                    return (
                      <div key={key} className="flex items-center gap-2">
                        <span className="text-xs text-grey-600 flex-1 truncate">{label}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          <span className="text-xs text-grey-400">$</span>
                          <input type="number" min="0" step="5" value={val} placeholder={String(DEFAULT_RATES[key])} onChange={e => handleRate(key, e.target.value)}
                            onBlur={e => { if (e.target.value === '' || Number(e.target.value) === 0) handleRate(key, DEFAULT_RATES[key]); }}
                            className="w-16 text-xs text-right border border-grey-200 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-darkblue-500" />
                          <span className="text-xs text-grey-400">/hr</span>
                        </div>
                        {isDefault && <span className="text-xs text-grey-300 shrink-0">default</span>}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-grey-400 mt-2 leading-snug">Berbay standard rates. Clearing a field restores the default.</p>
              </div>
              {totalAnnualHrs > 0 && (
                <div className="p-3 bg-grey-50 border border-grey-200 rounded-lg">
                  <p className="text-xs font-semibold text-grey-600 mb-2 flex items-center gap-1.5"><CheckCircleIcon className="w-3.5 h-3.5" /> Guideline Checks <span className="font-normal text-grey-400">(global mix)</span></p>
                  <div className="space-y-1.5">
                    {[{ label: `Principal < 5% (${principalPct.toFixed(0)}%)`, pass: checks.principalUnder5 }, { label: `Freelance < 15% (${freelancePct.toFixed(0)}%)`, pass: checks.freelanceUnder15 }, { label: `Sr/AM/AAM < 70% (${seniorPct.toFixed(0)}%)`, pass: checks.seniorUnder70 }, { label: `Strategic 25–35% (${strategicPct}%)`, pass: checks.strategicInRange }].map(({ label, pass }) => (
                      <div key={label} className="flex items-center gap-1.5"><GuidelineDot pass={pass} /><span className="text-xs text-grey-600">{label}</span></div>
                    ))}
                  </div>
                  {Object.values(checks).every(Boolean) && <div className="mt-2 flex items-center gap-1 text-xs font-medium text-teal-700"><CheckCircleSolid className="w-3.5 h-3.5 text-teal-500" /> All guidelines met!</div>}
                </div>
              )}
              {feeByRole && (
                <div className="p-3 bg-grey-50 border border-grey-200 rounded-lg">
                  <p className="text-xs font-semibold text-grey-600 mb-2">Est. Fee by Role</p>
                  <div className="space-y-1">
                    {feeByRole.map(({ key, label, fee }) => (
                      <div key={key} className="flex items-center justify-between"><span className="text-xs text-grey-500">{label.split(' ').pop()}</span><span className="text-xs font-medium text-darkblue-500">${Math.round(fee).toLocaleString()}</span></div>
                    ))}
                    <div className="flex items-center justify-between pt-1.5 mt-1 border-t border-grey-200">
                      <span className="text-xs font-semibold text-darkblue-500">Annual Total</span>
                      <span className="text-xs font-bold text-darkblue-500">${Math.round(estimatedAnnualFee).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const Step3BaselineSelection = ({ onSelectBaseline, onNextStep, savedPresets = [], onSavePreset, onDeletePreset }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [customData, setCustomData] = useState(null);

  // Per-baseline monthly hour overrides: { baselineId: { serviceName: { canonicalRole: monthlyHrs } } }
  const [monthlyHoursOverrides, setMonthlyHoursOverrides] = useState(() =>
    Object.fromEntries(BASELINES.map(b => [b.id, buildDefaultMonthlyHours(b)]))
  );

  const updateMonthlyHours = useCallback((baselineId, newHours) => {
    setMonthlyHoursOverrides(prev => ({ ...prev, [baselineId]: newHours }));
  }, []);

  const canProceed = selectedId === 'custom' ? customData?.annualHrs > 0 : selectedId !== null;

  const handleConfirm = () => {
    if (!canProceed) return;

    if (selectedId === 'custom') {
      onSelectBaseline?.(customData);
    } else {
      const preset   = BASELINES.find(b => b.id === selectedId);
      const monthly  = monthlyHoursOverrides[selectedId];
      // Build services directly from monthly hours (no ×12)
      const services = buildServicesFromMonthlyHours(monthly);
      onSelectBaseline?.({
        ...preset,
        services: Object.fromEntries(
          Object.entries(monthly).map(([svcName, roleHrs]) => [
            svcName,
            {
              annualHrs:  Object.values(roleHrs).reduce((s, h) => s + (parseFloat(h) || 0) * 12, 0),
              monthlyHrs: Object.values(roleHrs).reduce((s, h) => s + (parseFloat(h) || 0), 0),
              roleHrs,       // monthly values keyed by canonical role
              _isMonthly: true,
            },
          ])
        ),
        _prebuiltServices: services, // Step 4 uses this directly
      });
    }

    onNextStep?.();
  };

  const selectedPreset = selectedId && selectedId !== 'custom' ? BASELINES.find(b => b.id === selectedId) : null;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-darkblue-500 mb-2 inline-flex items-center gap-2">
          Choose a Baseline
          <Tooltip width="w-80" content="Each preset is derived from real Berbay engagement data. All values can be adjusted in the next step — the baseline is just your starting point.">
            <button className="text-grey-300 hover:text-brightblue-600 transition-colors focus:outline-none" aria-label="About baselines">
              <InformationCircleIcon className="w-5 h-5" />
            </button>
          </Tooltip>
        </h2>
        <p className="text-sm sm:text-base text-grey-600">
          Select a pre-built service mix based on historical client profiles, or build your own
        </p>
      </div>

      <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 gap-4">
        {BASELINES.map(b => (
          <BaselineCard
            key={b.id}
            baseline={b}
            isSelected={selectedId === b.id}
            onSelect={setSelectedId}
            monthlyHours={monthlyHoursOverrides[b.id]}
            onMonthlyHoursUpdate={(newHours) => updateMonthlyHours(b.id, newHours)}
          />
        ))}
      </div>

      <div className="max-w-5xl mx-auto">
        <CustomBaselineEditor
          isSelected={selectedId === 'custom'}
          onSelect={setSelectedId}
          onDataChange={setCustomData}
          savedPresets={savedPresets}
          onSavePreset={onSavePreset}
          onDeletePreset={onDeletePreset}
        />
      </div>

      {selectedPreset && (
        <div className="max-w-5xl mx-auto p-4 bg-violet-50 border border-violet-200 rounded-lg flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-violet-700">Selected: {selectedPreset.label} Baseline ({selectedPreset.tag})</p>
            <p className="text-xs text-violet-600 mt-0.5">
              {monthlyHoursOverrides[selectedPreset.id] &&
                Object.values(monthlyHoursOverrides[selectedPreset.id])
                  .reduce((t, roles) => t + Object.values(roles).reduce((s, h) => s + (parseFloat(h) || 0), 0), 0)
                  .toFixed(1)
              } hrs/mo · ${selectedPreset.monthlyFee.toLocaleString()}/mo est.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-violet-600">
            <CheckCircleIcon className="w-4 h-4" /> All values adjustable in the next step
          </div>
        </div>
      )}

      {selectedId === 'custom' && customData?.annualHrs > 0 && (
        <div className="max-w-5xl mx-auto p-4 bg-grey-50 border border-darkblue-200 rounded-lg flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-darkblue-500">{customData.label}</p>
            <p className="text-xs text-grey-500 mt-0.5">{customData.annualHrs.toFixed(1)} hrs/yr · ${customData.monthlyFee.toLocaleString()}/mo est. · ${Math.round(customData.annualFee).toLocaleString()}/yr est.</p>
          </div>
          <div className="flex items-center gap-2 text-xs text-teal-600"><CheckCircleIcon className="w-4 h-4" /> Ready to use as your starting baseline</div>
        </div>
      )}

      <div className="max-w-5xl mx-auto flex justify-end pt-2">
        <button onClick={handleConfirm} disabled={!canProceed}
          className={`w-full sm:w-auto px-8 py-2.5 text-sm font-semibold rounded-lg transition-all ${
            canProceed ? 'bg-brand-500 hover:bg-brand-dark text-white shadow-sm hover:shadow-md' : 'bg-grey-100 text-grey-400 cursor-not-allowed'
          }`}>
          {!selectedId ? 'Select a Baseline'
            : selectedId === 'custom' && !canProceed ? 'Enter service hours to continue'
            : selectedId === 'custom' ? 'Use Custom Baseline →'
            : 'Use This Baseline →'}
        </button>
      </div>
    </div>
  );
};

export default Step3BaselineSelection;